import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { DoraLevel, TrendDirection } from '@/lib/dora-utils';
import {
  describeSeries,
  EmptyChartMessage,
  isFullySeriesEmpty,
  shouldShowDots,
  Sparkline,
  StabilityAnnotation,
  type ISparkPoint,
} from './metric-card-sparkline';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

const TIER_BORDER_LEFT: Record<DoraLevel, string> = {
  elite: 'border-l-4 border-l-success',
  high: 'border-l-4 border-l-primary',
  medium: 'border-l-4 border-l-warning',
  low: 'border-l-4 border-l-error',
};

// Tinted-chip classes — same pattern as `TierList` on /metric-catalogue and the
// admin metric-definitions page so the tier verdict reads as the same visual
// vocabulary across the app.
const TIER_BADGE: Record<DoraLevel, string> = {
  elite: 'bg-success/15 text-success',
  high: 'bg-primary/15 text-primary',
  medium: 'bg-warning/15 text-warning',
  low: 'bg-error/15 text-error',
};

const TIER_LABEL: Record<DoraLevel, string> = {
  elite: 'ELITE',
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IDoraSparkPoint = ISparkPoint;

export interface IDoraDelta {
  direction: TrendDirection;
  label: string;
}

interface DoraMetricCardProps {
  title: string;
  definition: string;
  metricKey: string;
  unit: string;
  formatValue: (n: number) => string;
  series: ISparkPoint[];
  latestValue: number | null;
  level: DoraLevel | null;
  delta: IDoraDelta | null;
  eliteHint: string;
  latestLabel?: string;
  /**
   * When supplied AND `latestValue === null` AND every `series.value` is null,
   * the chart is replaced by this copy. Lets MTTR render an affirmative
   * "no incidents" state instead of an empty chart that reads as broken.
   */
  emptyMessage?: string;
  onViewDetails?: () => void;
  /** Forwarded to `Sparkline` — see `bucket-format.ts`. */
  axis?: IBucketAxisProps;
}

// ---------------------------------------------------------------------------
// Subviews
// ---------------------------------------------------------------------------

function CardHeader({
  title,
  definition,
  metricKey,
}: Readonly<{ title: string; definition: string; metricKey: string }>) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <MetricInfoPopover metricKey={metricKey} />
      </div>
      <p className="text-xs italic text-muted-foreground">{definition}</p>
    </div>
  );
}

function ValueBlock({
  latestValue,
  formatValue,
  unit,
  delta,
  level,
  latestLabel,
  hideBigNumber,
}: Readonly<{
  latestValue: number | null;
  formatValue: (n: number) => string;
  unit: string;
  delta: IDoraDelta | null;
  level: DoraLevel | null;
  latestLabel: string;
  /**
   * When true, only the period sublabel renders. Used by the MTTR no-incidents
   * empty state — the affirmative copy below replaces the big number, so
   * showing an em-dash here would be redundant noise.
   */
  hideBigNumber: boolean;
}>) {
  const valueText = latestValue === null ? '—' : formatValue(latestValue);

  let deltaNode: React.ReactNode = null;
  if (delta && !hideBigNumber) {
    const downSymbol = delta.direction === 'down' ? '▼' : '→';
    const symbol = delta.direction === 'up' ? '▲' : downSymbol;
    deltaNode = (
      <span className="ml-2 text-xs text-muted-foreground">
        {symbol} {delta.label}
      </span>
    );
  }

  return (
    <div className="mt-3 flex items-end justify-between">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{latestLabel}</p>
        {!hideBigNumber && (
          <div className="flex items-baseline">
            <span className="text-2xl font-bold leading-none text-foreground">{valueText}</span>
            {unit && <span className="ml-1 text-xs text-muted-foreground">{unit}</span>}
            {deltaNode}
          </div>
        )}
      </div>
      {level && !hideBigNumber && (
        <span
          className={cn(
            'rounded px-2 py-0.5 text-base font-bold uppercase tracking-wider',
            TIER_BADGE[level],
          )}
        >
          {TIER_LABEL[level]}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Series shape, Sparkline, StabilityAnnotation, and EmptyChartMessage are
// shared with CodeQualityTrendCard via metric-card-sparkline.tsx.
// ---------------------------------------------------------------------------

function CardFooter({
  eliteHint,
  onViewDetails,
}: Readonly<{ eliteHint: string; onViewDetails?: () => void }>) {
  return (
    <div className="mt-2 flex items-center justify-between border-t border-border/50 pt-2">
      <p className="text-[10px] text-muted-foreground">
        <span className="font-semibold text-success">●</span> {eliteHint}
      </p>
      {onViewDetails && (
        <button
          type="button"
          onClick={onViewDetails}
          className="text-[10px] font-medium text-primary hover:underline"
        >
          View details →
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DoraMetricCard({
  title,
  definition,
  metricKey,
  unit,
  formatValue,
  series,
  latestValue,
  level,
  delta,
  eliteHint,
  latestLabel = 'Latest',
  emptyMessage,
  onViewDetails,
  axis,
}: Readonly<DoraMetricCardProps>) {
  const borderClass = level ? TIER_BORDER_LEFT[level] : '';
  const showEmptyState = emptyMessage !== undefined && isFullySeriesEmpty(latestValue, series);
  const shape = describeSeries(series);
  const showStabilityNote = !showEmptyState && shape.isStable;
  const showDots = showStabilityNote && shouldShowDots(series, shape);

  return (
    <Card className={cn('shadow-none', borderClass)}>
      <CardContent className="p-4 pt-4">
        <CardHeader title={title} definition={definition} metricKey={metricKey} />
        <ValueBlock
          latestValue={latestValue}
          formatValue={formatValue}
          unit={unit}
          delta={delta}
          level={level}
          latestLabel={latestLabel}
          hideBigNumber={showEmptyState}
        />
        {showEmptyState ? (
          <EmptyChartMessage message={emptyMessage} />
        ) : (
          <>
            <Sparkline series={series} formatValue={formatValue} unit={unit} showDots={showDots} axis={axis} />
            {showStabilityNote && <StabilityAnnotation count={shape.nonNullCount} />}
          </>
        )}
        <CardFooter eliteHint={eliteHint} onViewDetails={onViewDetails} />
      </CardContent>
    </Card>
  );
}
